import { useState, useEffect, useCallback } from 'react';
import { api } from '../../api';
import { formatDateTime } from '../../utils/dateUtils';

export default function SpotioSetupPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Credentials
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [connected, setConnected] = useState(false);
  const [testing, setTesting] = useState(false);

  // Stage Groups (cold/customer/lost) + user overrides
  const [stageCache, setStageCache] = useState(null);
  const [stageOverrides, setStageOverrides] = useState({}); // { stageId: 'cold'|'customer'|'ignore' }
  const [effectiveStageMap, setEffectiveStageMap] = useState({});
  const [discoveringStages, setDiscoveringStages] = useState(false);
  const [savingOverrides, setSavingOverrides] = useState(false);

  // Visit template selection (which activityTemplateIds count as "visits")
  const [visitTemplateIds, setVisitTemplateIds] = useState([]);
  const [savingTemplates, setSavingTemplates] = useState(false);

  // User mapping
  const [userMapData, setUserMapData] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [mappings, setMappings] = useState({});

  // KPI linking (TWO KPIs now)
  const [kpis, setKpis] = useState([]);
  const [coldKpiId, setColdKpiId] = useState('');
  const [customerKpiId, setCustomerKpiId] = useState('');
  const [syncEnabled, setSyncEnabled] = useState(false);

  // Sync
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, kpiList] = await Promise.all([
        api.getSpotioConfig(),
        api.getLeaderboardKpis(),
      ]);
      setConfig(cfg);
      setClientId(cfg.clientId || '');
      setSecret(cfg.secret || '');
      setColdKpiId(cfg.coldKpiId ? String(cfg.coldKpiId) : '');
      setCustomerKpiId(cfg.customerKpiId ? String(cfg.customerKpiId) : '');
      setSyncEnabled(cfg.syncEnabled || false);
      setConnected(!!(cfg.clientId && cfg.clientId !== ''));
      setStageCache(cfg.stageCache || null);
      setStageOverrides(cfg.stageOverrides || {});
      setEffectiveStageMap(cfg.effectiveStageMap || {});
      setVisitTemplateIds(cfg.visitTemplateIds || []);
      setKpis(Array.isArray(kpiList) ? kpiList.filter(k => k.input_method === 'api' || k.input_method === 'manual') : []);
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Test Connection ──
  async function handleTest() {
    setTesting(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.testSpotioConnection({ clientId, secret });
      if (result.success) {
        setConnected(true);
        setSuccess('Connected to SPOTIO');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Connection failed');
      }
    } catch (err) {
      setError(err.message || 'Connection failed');
    }
    setTesting(false);
  }

  // ── Discover Stage Groups ──
  async function handleDiscoverStages() {
    setDiscoveringStages(true);
    setError('');
    setSuccess('');
    try {
      const result = await api.discoverSpotioStages();
      setStageCache(result);
      // Rebuild effective map = discovery + existing overrides
      const effective = { ...(result.stageIdToBucket || {}) };
      for (const [sid, bucket] of Object.entries(stageOverrides)) {
        effective[sid] = bucket;
      }
      setEffectiveStageMap(effective);
      setSuccess(`Discovered ${result.sampledTypes} data object type(s) from ${result.totalActivities} activities`);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to discover stage groups');
    }
    setDiscoveringStages(false);
  }

  // ── Save Visit Templates ──
  async function handleSaveTemplates() {
    setSavingTemplates(true);
    setError('');
    try {
      await api.saveSpotioConfig({ visitTemplateIds });
      setSuccess('Visit templates saved. Click Re-Discover Stages to see updated counts.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save visit templates');
    }
    setSavingTemplates(false);
  }

  function toggleVisitTemplate(templateId) {
    setVisitTemplateIds(prev => {
      const set = new Set(prev);
      if (set.has(templateId)) set.delete(templateId);
      else set.add(templateId);
      return Array.from(set);
    });
  }

  // ── Save Stage Overrides ──
  async function handleSaveOverrides() {
    setSavingOverrides(true);
    setError('');
    try {
      await api.saveSpotioStageOverrides(stageOverrides);
      setSuccess('Stage classification saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save overrides');
    }
    setSavingOverrides(false);
  }

  // Change the bucket for a single stage (local state — save button commits)
  function handleStageBucketChange(stageId, bucket, defaultBucket) {
    setStageOverrides(prev => {
      const next = { ...prev };
      if (bucket === defaultBucket) {
        // Reverting to default — remove override
        delete next[stageId];
      } else {
        next[stageId] = bucket;
      }
      return next;
    });
    setEffectiveStageMap(prev => ({ ...prev, [stageId]: bucket }));
  }

  // ── Discover Users ──
  async function handleDiscoverUsers() {
    setLoadingUsers(true);
    setError('');
    try {
      const result = await api.getSpotioUsers();
      setUserMapData(result);
      const existing = {};
      for (const su of (result.spotioUsers || [])) {
        if (su.mappedTo) existing[su.ownerId] = String(su.mappedTo);
      }
      setMappings(existing);
    } catch (err) {
      setError(err.message || 'Failed to discover users');
    }
    setLoadingUsers(false);
  }

  // ── Save All Config ──
  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      await api.saveSpotioConfig({
        clientId,
        secret,
        syncEnabled,
        coldKpiId: coldKpiId ? parseInt(coldKpiId) : null,
        customerKpiId: customerKpiId ? parseInt(customerKpiId) : null,
      });

      if (userMapData) {
        const mapArray = Object.entries(mappings)
          .filter(([, userId]) => userId)
          .map(([ownerId, userId]) => ({ spotio_owner_id: ownerId, user_id: parseInt(userId) }));
        await api.saveSpotioUserMap(mapArray);
      }

      setSuccess('SPOTIO configuration saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save');
    }
    setSaving(false);
  }

  // ── Manual Sync ──
  async function handleSync() {
    setSyncing(true);
    setError('');
    try {
      const result = await api.triggerSpotioSync();
      if (result.synced) {
        const coldSummary = result.cold?.kpiName
          ? `Cold: ${result.cold.repsUpdated} reps / ${result.cold.totalVisits} visits`
          : '';
        const custSummary = result.customer?.kpiName
          ? `Customer: ${result.customer.repsUpdated} reps / ${result.customer.totalVisits} visits`
          : '';
        setSuccess(`Sync complete (${result.period}) — ${[coldSummary, custSummary].filter(Boolean).join(' · ')}`);
      } else {
        setError(result.reason || 'Sync not ready');
      }
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      setError(err.message || 'Sync failed');
    }
    setSyncing(false);
  }

  if (loading) return <div className="admin-page"><div className="loading-spinner" /></div>;

  const lastSync = config?.lastSync ? formatDateTime(config.lastSync) : 'Never';
  const lastResult = config?.lastSyncResult;

  // Build unified list of discovered stages with their default classification
  // (from SPOTIO's active/won/lost groups) and any current override.
  const groupsArray = stageCache?.groups ? Object.values(stageCache.groups) : [];
  const activityStageCounts = stageCache?.activityStageCounts || {};
  const allDiscoveredStages = [];
  for (const g of groupsArray) {
    for (const s of (g.active || [])) allDiscoveredStages.push({ ...s, defaultBucket: 'cold',     typeName: g.typeName });
    for (const s of (g.won || []))    allDiscoveredStages.push({ ...s, defaultBucket: 'customer', typeName: g.typeName });
    for (const s of (g.lost || []))   allDiscoveredStages.push({ ...s, defaultBucket: 'ignore',   typeName: g.typeName });
  }
  // Deduplicate by stage id (in case two types share a stage)
  const seenIds = new Set();
  const uniqueStages = allDiscoveredStages.filter(s => {
    if (seenIds.has(s.id)) return false;
    seenIds.add(s.id);
    return true;
  });

  // Attach activity counts and sort by count descending so the most-active
  // stages surface at the top (helpful when matching unlabeled stage IDs).
  uniqueStages.forEach(s => {
    s.activityCount = activityStageCounts[s.id]?.count || 0;
  });
  uniqueStages.sort((a, b) => b.activityCount - a.activityCount);

  // Counts per effective bucket (for header summary)
  const bucketCounts = { cold: 0, customer: 0, ignore: 0 };
  for (const s of uniqueStages) {
    const effective = effectiveStageMap[s.id] || s.defaultBucket;
    if (bucketCounts[effective] !== undefined) bucketCounts[effective]++;
  }

  const workflowReturnedNothing = stageCache && stageCache.workflowStageCount === 0;


  return (
    <div className="admin-page">
      <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 4 }}>SPOTIO Integration</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 24 }}>
        Connect to SPOTIO to pull visit counts into two leaderboards: Cold Contact Visits (prospects in progress) and Customer Visits (closed accounts).
      </p>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}

      {/* ─── STEP 1: Credentials ─── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#6d28d915', color: '#6d28d9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900 }}>1</span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Connect to SPOTIO</h2>
          {connected && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#dcfce7', color: '#16a34a' }}>Connected</span>}
        </div>

        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Enter your SPOTIO Client ID and Secret from Settings &rarr; Integrations &rarr; API in your SPOTIO account.
        </p>

        <div className="form-grid">
          <div className="form-group">
            <label>Client ID</label>
            <input className="form-input" value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Your SPOTIO Client ID" />
          </div>
          <div className="form-group">
            <label>Secret Key</label>
            <input className="form-input" type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="Your SPOTIO Secret" />
          </div>
        </div>

        <button className="btn btn-gold" onClick={handleTest} disabled={testing || !clientId || !secret}>
          {testing ? 'Testing...' : 'Test Connection'}
        </button>
      </div>

      {/* ─── STEP 2: Discover Stage Groups ─── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20, opacity: connected ? 1 : 0.5, pointerEvents: connected ? 'auto' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#6d28d915', color: '#6d28d9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900 }}>2</span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Discover Pipeline Stages</h2>
          {stageCache && <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 6, background: '#dcfce7', color: '#16a34a' }}>Cached</span>}
        </div>

        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Scan recent SPOTIO activities and pull your pipeline's stage groups. SPOTIO categorizes stages as <b>Active</b> (prospects in progress), <b>Won</b> (customers), and <b>Lost</b> (dead deals). Active stages will feed the Cold Contact Visits board; Won stages feed the Customer Visits board; Lost stages are ignored.
        </p>

        <button className="btn btn-outline" onClick={handleDiscoverStages} disabled={discoveringStages} style={{ marginBottom: 16 }}>
          {discoveringStages ? 'Discovering...' : (stageCache ? 'Re-Discover Stages' : 'Discover Stages')}
        </button>

        {stageCache && (
          <>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
              Period: <b>{stageCache.periodLabel || 'Current month'}</b> &middot; {stageCache.totalActivities} visit(s){stageCache.rawActivities ? ` (${stageCache.rawActivities} raw activities)` : ''} &middot; {stageCache.sampledTypes} data object type(s) &middot; Cached {stageCache.cachedAt ? formatDateTime(stageCache.cachedAt) : '—'}
            </div>

            {/* ─── Visit Template Picker ─── */}
            {stageCache.templateCatalog && Object.keys(stageCache.templateCatalog).length > 0 && (
              <div style={{ marginBottom: 20, padding: 14, border: '1px solid #e2e8f0', borderRadius: 10, background: '#fafafa' }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: '#0f172a', marginBottom: 4, textTransform: 'uppercase' }}>
                  Which activity templates count as "visits"?
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                  SPOTIO's activity feed includes every type (Visits, Drop Off Materials, Calls, Notes, etc.). Check only the ones that should be counted as visits on your leaderboard. Match the numbers to SPOTIO's native "Visits" report.
                </div>
                <div style={{ display: 'grid', gap: 6 }}>
                  {Object.values(stageCache.templateCatalog)
                    .sort((a, b) => (b.done + b.scheduled) - (a.done + a.scheduled))
                    .map(tmpl => {
                      const checked = visitTemplateIds.includes(tmpl.id);
                      return (
                        <label key={tmpl.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                          borderRadius: 8, border: checked ? '2px solid #6d28d9' : '1px solid #e2e8f0',
                          background: checked ? '#f5f3ff' : '#fff', cursor: 'pointer',
                        }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleVisitTemplate(tmpl.id)}
                            style={{ width: 16, height: 16 }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                              {tmpl.title}
                            </div>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>
                              Template ID: {tmpl.id}
                            </div>
                          </div>
                          <div style={{ fontSize: 11, color: '#64748b', textAlign: 'right' }}>
                            <div><b style={{ color: '#0f172a' }}>{tmpl.done}</b> completed</div>
                            {tmpl.scheduled > 0 && (
                              <div style={{ color: '#f59e0b' }}>{tmpl.scheduled} scheduled</div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                </div>
                <div style={{ marginTop: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
                  <button
                    className="btn btn-gold"
                    onClick={handleSaveTemplates}
                    disabled={savingTemplates}
                    style={{ fontSize: 12 }}
                  >
                    {savingTemplates ? 'Saving...' : 'Save Visit Templates'}
                  </button>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>
                    After saving, click Re-Discover Stages to see the filtered counts.
                  </div>
                </div>
              </div>
            )}

            {/* Summary chips */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, background: '#dbeafe', color: '#1d4ed8' }}>❄️ Cold: {bucketCounts.cold}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, background: '#dcfce7', color: '#15803d' }}>✅ Customer: {bucketCounts.customer}</span>
              <span style={{ fontSize: 11, fontWeight: 700, padding: '5px 10px', borderRadius: 6, background: '#f1f5f9', color: '#64748b' }}>🚫 Ignored: {bucketCounts.ignore}</span>
            </div>

            {workflowReturnedNothing && (
              <div style={{ marginBottom: 14, padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                <strong>Workflow stages endpoint returned no data.</strong> Falling back to stages extracted directly from your activities. Stage names won't be available — match them to your real pipeline stages by activity count, then classify each as Cold / Customer / Ignore using the dropdowns below.
              </div>
            )}

            {/* Per-stage classification list */}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 110px 160px', gap: 12,
                padding: '10px 14px', background: '#f8fafc', fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase',
              }}>
                <div>Stage</div>
                <div style={{ textAlign: 'right' }}>Visits (this month)</div>
                <div>Classification</div>
              </div>
              {uniqueStages.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                  No stages discovered. Click Re-Discover Stages above.
                </div>
              ) : uniqueStages.map(s => {
                const effective = effectiveStageMap[s.id] || s.defaultBucket;
                const isOverride = effective !== s.defaultBucket;
                return (
                  <div key={s.id} style={{
                    display: 'grid', gridTemplateColumns: '1fr 110px 160px', gap: 12, alignItems: 'center',
                    padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
                  }}>
                    <div>
                      <span style={{
                        display: 'inline-block', fontSize: 12, padding: '4px 10px', borderRadius: 6,
                        background: s.color || '#6d28d9', color: '#fff', fontWeight: 700,
                      }}>{s.name}</span>
                      {isOverride && (
                        <span style={{ marginLeft: 8, fontSize: 10, color: '#f59e0b', fontWeight: 700 }}>
                          OVERRIDE
                        </span>
                      )}
                      <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3, fontFamily: 'monospace' }}>
                        {s.id}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', fontSize: 13, fontWeight: 700, color: '#0f172a', paddingRight: 8 }}>
                      {s.activityCount.toLocaleString()}
                    </div>
                    <select
                      value={effective}
                      onChange={e => handleStageBucketChange(s.id, e.target.value, s.defaultBucket)}
                      className="form-input"
                      style={{ fontSize: 12, padding: '6px 8px' }}
                    >
                      <option value="cold">❄️ Cold Contact</option>
                      <option value="customer">✅ Customer</option>
                      <option value="ignore">🚫 Ignore</option>
                    </select>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                className="btn btn-gold"
                onClick={handleSaveOverrides}
                disabled={savingOverrides}
                style={{ fontSize: 12 }}
              >
                {savingOverrides ? 'Saving...' : 'Save Classification'}
              </button>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Override classifications persist across stage re-discovery.
              </div>
            </div>

            {lastResult?.unknownStageIds?.length > 0 && (
              <div style={{ marginTop: 12, padding: 10, background: '#fef3c7', borderRadius: 8, fontSize: 11, color: '#92400e' }}>
                ⚠️ Last sync saw {lastResult.unknownStageIds.length} unknown stage ID(s) — a new stage may have been added in SPOTIO. Click "Re-Discover Stages" to refresh.
              </div>
            )}

            {stageCache.debugResponses?.length > 0 && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 11, color: '#64748b', fontWeight: 700 }}>
                  Debug: Raw workflow API responses ({stageCache.debugResponses.length})
                </summary>
                <pre style={{
                  marginTop: 8, padding: 12, background: '#0f172a', color: '#a5f3fc',
                  fontSize: 10, borderRadius: 8, overflow: 'auto', maxHeight: 300,
                }}>
                  {JSON.stringify(stageCache.debugResponses, null, 2)}
                </pre>
              </details>
            )}
          </>
        )}
      </div>

      {/* ─── STEP 3: Map Users ─── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20, opacity: connected ? 1 : 0.5, pointerEvents: connected ? 'auto' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#6d28d915', color: '#6d28d9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900 }}>3</span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Map SPOTIO Reps</h2>
        </div>

        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Match each SPOTIO rep to their user account here so activity counts go to the right person on the leaderboard.
        </p>

        <button className="btn btn-outline" onClick={handleDiscoverUsers} disabled={loadingUsers} style={{ marginBottom: 16 }}>
          {loadingUsers ? 'Loading SPOTIO users...' : 'Load SPOTIO Reps'}
        </button>

        {userMapData && (
          <>
            {userMapData.source && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
                Source: {userMapData.source}
                {userMapData.sampleKeys?.length > 0 && (
                  <span> &middot; Fields: {userMapData.sampleKeys.join(', ')}</span>
                )}
              </div>
            )}
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
              {userMapData.spotioUsers?.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No SPOTIO users found</div>
              ) : (
                userMapData.spotioUsers?.map(su => (
                  <div key={su.ownerId} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
                    borderBottom: '1px solid #f1f5f9',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>
                        {su.name || 'Unknown Rep'}
                      </div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>
                        {su.email || 'No email'}
                        {su.externalId && <span style={{ marginLeft: 8, color: '#94a3b8' }}>Ext: {su.externalId}</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {su.count} activities (30 days)
                      </div>
                    </div>
                    <select
                      value={mappings[su.ownerId] || ''}
                      onChange={e => setMappings(prev => ({ ...prev, [su.ownerId]: e.target.value }))}
                      className="form-input"
                      style={{ width: 200, fontSize: 13 }}
                    >
                      <option value="">-- Not mapped --</option>
                      {userMapData.localUsers?.map(u => (
                        <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                      ))}
                    </select>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* ─── STEP 4: Link to KPIs (TWO now) ─── */}
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#6d28d915', color: '#6d28d9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 900 }}>4</span>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>Link to Leaderboard KPIs</h2>
        </div>

        <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Link each bucket to its own KPI. Create API-type KPIs in KPI Setup first (monthly period recommended). Either field can be left empty if you only want one board.
        </p>

        <div className="form-grid">
          <div className="form-group">
            <label>❄️ Cold Contact Visits KPI</label>
            <select className="form-input" value={coldKpiId} onChange={e => setColdKpiId(e.target.value)}>
              <option value="">-- None --</option>
              {kpis.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.period_type})</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>✅ Customer Visits KPI</label>
            <select className="form-input" value={customerKpiId} onChange={e => setCustomerKpiId(e.target.value)}>
              <option value="">-- None --</option>
              {kpis.map(k => (
                <option key={k.id} value={k.id}>{k.name} ({k.period_type})</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={syncEnabled} onChange={e => setSyncEnabled(e.target.checked)} style={{ width: 18, height: 18 }} />
            <span style={{ fontWeight: 600, fontSize: 14 }}>Enable auto-sync every 15 minutes</span>
          </label>
        </div>
      </div>

      {/* ─── Actions ─── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <button className="btn btn-gold" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
        <button
          className="btn btn-outline"
          onClick={handleSync}
          disabled={syncing || (!coldKpiId && !customerKpiId) || !stageCache}
        >
          {syncing ? 'Syncing...' : 'Sync Now'}
        </button>
      </div>

      {/* ─── Last Sync Info ─── */}
      {config && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, fontSize: 12, color: '#64748b' }}>
          <div><strong>Last sync:</strong> {lastSync}</div>
          {lastResult && (
            <div style={{ marginTop: 6 }}>
              <strong>Period:</strong> {lastResult.period}
              {lastResult.cold?.kpiName && (
                <span> &middot; <span style={{ color: '#1d4ed8', fontWeight: 700 }}>Cold:</span> {lastResult.cold.repsUpdated} reps / {lastResult.cold.totalVisits} visits</span>
              )}
              {lastResult.customer?.kpiName && (
                <span> &middot; <span style={{ color: '#15803d', fontWeight: 700 }}>Customer:</span> {lastResult.customer.repsUpdated} reps / {lastResult.customer.totalVisits} visits</span>
              )}
              {lastResult.ignoredVisits > 0 && (
                <span style={{ color: '#94a3b8' }}> &middot; Ignored: {lastResult.ignoredVisits}</span>
              )}
              {lastResult.unmappedOwnerIds?.length > 0 && (
                <span style={{ color: '#f59e0b', fontWeight: 600 }}> &middot; {lastResult.unmappedOwnerIds.length} unmapped rep(s)</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
